import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI, aanvragenAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  TrendingUp, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Phone,
  ArrowRight,
  UserPlus,
  Filter
} from 'lucide-react';

const Sales = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState({ team_members: [], unassigned_leads: 0 });
  const [selectedMember, setSelectedMember] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    fetchTeamOverview();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [selectedMember]);

  const fetchTeamOverview = async () => {
    try {
      setLoading(true);
      const res = await salesAPI.getTeamOverview();
      setTeamData(res.data);
    } catch (error) {
      console.error('Error fetching team overview:', error);
      toast.error('Kon sales overzicht niet laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoadingLeads(true);
      const params = {};
      if (selectedMember && selectedMember !== 'all' && selectedMember !== 'unassigned') {
        params.assigned_to = selectedMember;
      }
      const res = await aanvragenAPI.getAll(params);
      
      // Filter unassigned if selected
      let filteredLeads = res.data;
      if (selectedMember === 'unassigned') {
        filteredLeads = res.data.filter(l => !l.assigned_to);
      }
      
      setLeads(filteredLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoadingLeads(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Nieuw</Badge>;
      case 'contacted':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Gecontacteerd</Badge>;
      case 'converted':
        return <Badge variant="secondary" className="bg-green-100 text-green-700">Geconverteerd</Badge>;
      case 'rejected':
        return <Badge variant="secondary" className="bg-red-100 text-red-700">Afgewezen</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
      case 'sales':
        return <Badge className="bg-blue-100 text-blue-700">Sales</Badge>;
      case 'cleaner':
        return <Badge className="bg-orange-100 text-orange-700">Install/Clean</Badge>;
      case 'technician':
        return <Badge className="bg-green-100 text-green-700">Technieker</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="sales-overview-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Sales Overzicht</h1>
          <p className="text-muted-foreground">Beheer leads en bekijk team prestaties</p>
        </div>
        <Button onClick={() => navigate('/aanvragen')} data-testid="view-all-leads-btn">
          Alle Aanvragen <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Teamleden</p>
                <p className="text-2xl font-bold">{teamData.team_members.length}</p>
              </div>
              <Users className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Niet Toegewezen</p>
                <p className="text-2xl font-bold text-orange-600">{teamData.unassigned_leads}</p>
              </div>
              <UserPlus className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totaal Toegewezen</p>
                <p className="text-2xl font-bold">
                  {teamData.team_members.reduce((acc, m) => acc + m.lead_stats.total, 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Gem. Conversie</p>
                <p className="text-2xl font-bold">
                  {teamData.team_members.length > 0 
                    ? Math.round(teamData.team_members.reduce((acc, m) => acc + m.conversion_rate, 0) / teamData.team_members.length)
                    : 0}%
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Prestaties</CardTitle>
          <CardDescription>Overzicht van leads per teamlid</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="text-center">Nieuw</TableHead>
                <TableHead className="text-center">Gecontacteerd</TableHead>
                <TableHead className="text-center">Geconverteerd</TableHead>
                <TableHead className="text-center">Afgewezen</TableHead>
                <TableHead className="text-center">Totaal</TableHead>
                <TableHead className="text-center">Conversie %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamData.team_members.map((member) => (
                <TableRow 
                  key={member.id} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedMember(member.id)}
                >
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell>{getRoleBadge(member.role)}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-blue-50">{member.lead_stats.new}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-yellow-50">{member.lead_stats.contacted}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-green-50">{member.lead_stats.converted}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-red-50">{member.lead_stats.rejected}</Badge>
                  </TableCell>
                  <TableCell className="text-center font-bold">{member.lead_stats.total}</TableCell>
                  <TableCell className="text-center">
                    <Badge 
                      variant="secondary" 
                      className={member.conversion_rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-gray-100'}
                    >
                      {member.conversion_rate}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Leads Table with Filter */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle>Leads</CardTitle>
              <CardDescription>Filter en bekijk leads per teamlid</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedMember} onValueChange={setSelectedMember}>
                <SelectTrigger className="w-[200px]" data-testid="leads-filter-select">
                  <SelectValue placeholder="Filter op medewerker" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Leads</SelectItem>
                  <SelectItem value="unassigned">Niet Toegewezen</SelectItem>
                  {teamData.team_members.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingLeads ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Geen leads gevonden voor dit filter
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Naam</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefoon</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Toegewezen aan</TableHead>
                  <TableHead>Aangemaakt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.slice(0, 20).map((lead) => (
                  <TableRow 
                    key={lead.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate('/aanvragen')}
                  >
                    <TableCell className="font-medium">
                      {lead.first_name} {lead.last_name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{lead.email}</TableCell>
                    <TableCell>
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(lead.status)}</TableCell>
                    <TableCell>
                      {lead.assigned_to_name || (
                        <span className="text-orange-600 text-sm">Niet toegewezen</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(lead.created_at).toLocaleDateString('nl-BE')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {leads.length > 20 && (
            <div className="text-center mt-4">
              <Button variant="outline" onClick={() => navigate('/aanvragen')}>
                Bekijk alle {leads.length} leads
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Sales;
